<template>
  <div class="d-flex-column justify-start align-start gap-18">
    <div class="d-flex-row justify-start md-row-to-col flex-wrap gap-18">
      <div class="d-flex-row justify-start flex-3">
        <DropDown
          :placeholder="$t('find_by_realm')"
          :drop-items="getKeyedServers"
          :selected-item="filters.realm"
          variant="transparent"
          :select-drop-item="selectItem"
          item-key="realm"
          :show-search="true"
          :show-icon="true"
        />
      </div>
      <!-- SERVER TAB -->
      <div class="d-flex-row space-between gold_filter_tab gap-10">
        <button
          class="flex-2 t-grey font-family-2 medium-l"
          :class="[filters.server === null && 'active']"
          @click.stop="selectItem('server', null)"
        >
          {{ $t("new.all") }}
        </button>
        <button
          v-for="server in getGamePack?.servers"
          class="flex-3 t-grey font-family-2 medium-l"
          :key="server"
          :class="[filters.server === server.id && 'active']"
          @click.stop="selectItem('server', server.id)"
        >
          {{ server.title }}
        </button>
      </div>
      <!-- FACTION TAB -->
      <div class="d-flex-row space-between flex-2 gold_filter_tab gap-10">
        <button
          class="flex-2 t-grey font-family-2 medium-l"
          :class="[filters.faction === null && 'active']"
          @click.stop="selectItem('faction', null)"
        >
          {{ $t("new.all") }}
        </button>
        <button
          v-for="faction in getGame?.factions"
          class="flex-3 t-grey font-family-2 medium-l"
          :key="faction"
          :class="[filters.faction === faction.id && 'active']"
          @click.stop="selectItem('faction', faction.id)"
        >
          {{ faction.title }}
        </button>
      </div>
      <div class="d-flex-row justify-start flex-2">
        <label class="t-white font-family-2 medium-m">{{
          $t("new.seller_online")
        }}</label>
        <Switch v-model="filters.online" variant="primary" />
      </div>
    </div>
    <div class="d-flex-row space-between">
      <div class="d-flex-row justify-start flex-5">
        <label class="regular-m font-family-2 t-grey"
          >{{ proposals.length }} {{ $t("new.items_found") }}</label
        >
      </div>
      <div class="d-flex-row justify-end flex-1">
        <DropDown
          :drop-items="sortBys"
          item-key="orderBy"
          :selected-item="filters.orderBy"
          :select-drop-item="selectItem"
          variant="transparent small"
        />
      </div>
    </div>

    <div
      class="d-flex-column justify-start align-start gold_list_container b-radius-9 md-hide"
    >
      <Table
        :items="proposals"
        :table-columns="goldBoostColumns"
        variant="secondary"
        :open-detail="openProposal"
      />
    </div>
    <div class="d-flex-column justify-start align-start above-md-hide">
      <div
        class="d-flex-column justify-start align-start gap-15 gold_proposal_card b-radius-6"
        v-for="proposal in proposals"
        :key="proposal"
        @click.stop="(e) => openProposal(e, proposal)"
      >
        <GoldSeller
          :avatar="proposal.user_avatar"
          :username="proposal.user_username"
          :review-percentage="proposal.positive_reviews"
          :total-reviews="proposal.total_reviews"
          :online="proposal.user_online"
        />
        <div class="d-flex-column justify-start align-start gap-5">
          <div class="d-flex-row justify-start">
            <div class="d-flex-row justify-start flex-5">
              <label class="t-light font-family-2 regular-l">{{
                $t("new.price")
              }}</label>
            </div>
            <div class="d-flex-row justify-start flex-5">
              <GoldPrice :price="proposal.price" />
            </div>
          </div>
          <div class="d-flex-row justify-start">
            <div class="d-flex-row justify-start flex-5">
              <label class="t-light font-family-2 regular-l">{{
                $t("new.in_stock")
              }}</label>
            </div>
            <div class="d-flex-row justify-start flex-5">
              <label class="medium-l font-family-2 t-white">{{
                proposal?.in_stock
              }}</label>
            </div>
          </div>
          <div class="d-flex-row justify-start">
            <div class="d-flex-row justify-start flex-5">
              <label class="t-light font-family-2 regular-l">{{
                $t("new.min_purchase")
              }}</label>
            </div>
            <div class="d-flex-row justify-start flex-5">
              <label class="medium-l font-family-2 t-white">{{
                proposal?.min_purchase
              }}</label>
            </div>
          </div>
        </div>
        <GoldDetail
          :servers="proposal.servers"
          :factions="proposal.factions"
          :realms="proposal.realms"
        />
      </div>
    </div>

    <div class="d-flex-row">
      <Pagination
        :current-page="page"
        :total-pages="totalProposals"
        :max-pages="3"
        @pagechanged="onPageChange"
      />
    </div>
  </div>
  <Modal :is-open="Boolean(activeGoldProposal)" :toggle="closeProposal">
    <GoldBoostCheckout
      :close-checkout="closeProposal"
      :propsal-data="activeProposalDetails"
  /></Modal>
</template>

<script setup>
import DropDown from "~/components/custom/DropDown.vue";
import Switch from "~/components/custom/Switch.vue";
import Table from "~/components/custom/Table.vue";
import GoldSeller from "~/components/common/table/GoldSeller.vue";
import GoldPrice from "~/components/common/table/GoldPrice.vue";
import GoldDetail from "~/components/common/table/GoldDetail.vue";
import Pagination from "~/components/custom/Pagination.vue";
import Modal from "~/components/common/Modal.vue";
import GoldBoostCheckout from "./GoldBoostCheckout.vue";
import { ref, computed, watch, onMounted } from "vue";

// HOOKS
const { getGamePack, getGame } = useGameStore();

const checkoutStore = useCheckoutStore();
const { getLocale } = useFunctions();
const { t } = useI18n();

const goldBoostColumns = computed(() => {
  return [
    {
      id: "gold_seller",
      label: t("new.seller"),
      minWidth: "100px",
      align: "start",
    },
    {
      id: "gold_price",
      label: t("new.price"),
      minWidth: "100px",
      align: "start",
    },
    {
      id: "gold_stock",
      label: t("new.in_stock"),
      minWidth: "50px",
      align: "start",
    },
    {
      id: "gold_min_purchase",
      label: t("new.min_purchase"),
      minWidth: "50px",
      align: "start",
    },
    {
      id: "gold_servers",
      label: t("calculators_common.servers"),
      minWidth: "80px",
      align: "start",
    },
    {
      id: "gold_factions",
      label: t("new.factions"),
      minWidth: "80px",
      align: "start",
    },
    {
      id: "gold_realms",
      label: t("new.realms"),
      minWidth: "80px",
      align: "start",
    },
  ];
});

const filters = ref({
  // realm: 11,
  server: null,
  faction: null,
  online: false,
  orderBy: "price",
  realm: null,
});
const proposals = ref([]);
const totalProposals = ref(0);
const sortBys = ref({
  price: t("new.sort_by_price"),
});

const activeGoldProposal = ref("");
const activeProposalDetails = ref({});
const { currentGameSlug } = useGameComposable();
const page = ref(1);
const realms = ref([]);
const LIMIT = 10;

const getKeyedServers = computed(() => {
  if (realms.value) {
    return realms.value.reduce((acc, val) => {
      return { ...acc, [val.id]: val.title };
    }, {});
  }
  return {};
});

const onPageChange = (pg) => {
  page.value = pg;
};

const selectItem = (key, value) => {
  if (key === "server") {
    checkoutStore.setServer(value);
  }
  filters.value = {
    ...filters.value,
    [key]: value,
  };
};

const openProposal = (e, proposal) => {
  activeGoldProposal.value = proposal.proposal_code;
  activeProposalDetails.value = {
    ...proposal,
  };
};
const closeProposal = () => {
  activeGoldProposal.value = "";
  activeProposalDetails.value = {};
};
const fetchData = async () => {
  try {
    const { data: response } = await useApiPost(
      "/api/get-mmo-game-currency-proposals",
      {
        method: "POST",
        body: {
          game: currentGameSlug,
          filters: {
            ...filters.value,
          },
          page: page.value,
          count: LIMIT,
        },
      }
    );
    if (response.proposals) {
      // totalProposals.value = response.total_proposals;
      totalProposals.value = Math.ceil(response.total_proposals / LIMIT);
      proposals.value = response.proposals;
    }
  } catch (error) {
    console.log(error);
  }
};

await fetchData();
watch(
  filters,
  (newVal) => {
    fetchData();
  },
  { deep: true }
);

const handleDetails = () => {
  checkoutStore.clearCheckout();
  checkoutStore.setServer(1);
  checkoutStore.setPlatform({
    platform: 1,
  });
  checkoutStore.setGameAndPack({
    game: getGame.id,
    pack: getGamePack.id,
    fullGame: getGame,
    fullPack: getGamePack,
  });
};
handleDetails();
onMounted(() => {
  checkoutStore.setCalculating();
});

const fetchGetMmoRealms = async () => {
  try {
    const { data: realmResponse } = await useApiFetch(
      "/api/get-mmo-game-realms",
      {
        method: "POST",
        fetchKey: "get-mmo-game-realms",
        body: {
          locale: getLocale(),
          game: "world-of-warcraft-boost",
          server: filters.value.server,
        },
      }
    );
    if (realmResponse.value.data.realms) {
      realms.value = realmResponse.value.data.realms;
    }
  } catch (error) {
    console.log(error);
  }
};
await fetchGetMmoRealms();

watch(page, () => {
  fetchData();
});
</script>
